import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { db, doc, getDoc } from "../../lib/firebase";
import { SKILLS_INVENTORY, SKILL_LEVELS } from "../../data/career-skills";
import certsData from "../../data/certifications.json";
import { LEARNING_INTEREST_GROUPS } from "../../data/learning-interests";
import { submitJuniorOnboarding } from "../../lib/functions";
import Instructions from "../../components/Instructions/Instructions";

// ─── Styled components ────────────────────────────────────────────────────────

const Section = styled.section`
  padding: 112px 0 96px;
  background: var(--nd-black);
`;

const Card = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const Heading = styled.h2`
  font-family: "Space Grotesk", sans-serif;
  font-size: 28px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  margin: 0;
`;

const Sub = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 8px;
`;

const StepIndicator = styled.div`
  font-family: "Space Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
`;

const Input = styled.input`
  font-family: "Space Grotesk", sans-serif;
  font-size: 15px;
  background: var(--nd-black);
  color: var(--nd-text-display);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 44px;
  &:focus {
    outline: none;
    border-color: var(--nd-text-display);
  }
`;

const Select = styled.select`
  font-family: "Space Grotesk", sans-serif;
  font-size: 15px;
  background: var(--nd-black);
  color: var(--nd-text-display);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 44px;
  &:focus {
    outline: none;
    border-color: var(--nd-text-display);
  }
`;

const Textarea = styled.textarea`
  font-family: "Space Grotesk", sans-serif;
  font-size: 15px;
  background: var(--nd-black);
  color: var(--nd-text-display);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 12px 14px;
  min-height: 120px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: var(--nd-text-display);
  }
`;

const CharCount = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 10px;
  color: var(--nd-text-disabled);
  letter-spacing: 0.04em;
  text-align: right;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  font-family: "Space Mono", monospace;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${(p) => (p.secondary ? "transparent" : "var(--nd-accent)")};
  color: ${(p) => (p.secondary ? "var(--nd-text-secondary)" : "var(--nd-text-display)")};
  border: ${(p) => (p.secondary ? "1px solid var(--nd-border-visible)" : "none")};
  border-radius: 999px;
  padding: 14px 28px;
  min-height: 44px;
  cursor: pointer;
  transition: opacity 200ms ease;
  &:hover:not(:disabled) {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Skill assessment row
const SkillRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--nd-border);
  flex-wrap: wrap;
`;

const SkillName = styled.span`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  color: var(--nd-text-display);
  flex: 1;
  min-width: 140px;
`;

const LevelButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const LevelBtn = styled.button`
  font-family: "Space Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.active ? "var(--nd-accent)" : "var(--nd-border-visible)")};
  background: ${(p) => (p.active ? "var(--nd-accent)" : "transparent")};
  color: ${(p) => (p.active ? "var(--nd-text-display)" : "var(--nd-text-secondary)")};
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease;
  &:hover {
    border-color: var(--nd-accent);
  }
`;

// Cert cycle button
const CertGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CertRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--nd-border);
  flex-wrap: wrap;
`;

const CertInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const CertName = styled.span`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  color: var(--nd-text-display);
`;

const CertIssuer = styled.span`
  font-family: "Space Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.05em;
  color: var(--nd-text-secondary);
  text-transform: uppercase;
`;

const STATUS_ORDER = ["to_be_started", "in_progress", "achieved"];
const STATUS_LABEL = {
  to_be_started: "Not started",
  in_progress: "In progress",
  achieved: "Achieved",
};
const STATUS_COLOR = {
  to_be_started: "var(--nd-text-disabled)",
  in_progress: "var(--nd-accent)",
  achieved: "var(--nd-success, #22c55e)",
};

const StatusBadge = styled.button`
  font-family: "Space Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${(p) => STATUS_COLOR[p.status] || "var(--nd-border-visible)"};
  background: transparent;
  color: ${(p) => STATUS_COLOR[p.status] || "var(--nd-text-secondary)"};
  cursor: pointer;
  transition: opacity 150ms ease;
  &:hover {
    opacity: 0.75;
  }
`;

const Message = styled.p`
  font-family: "Space Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  margin: 0;
  color: ${(p) => (p.kind === "error" ? "var(--nd-accent)" : "var(--nd-success, #22c55e)")};
`;

const GateMessage = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 15px;
  color: var(--nd-text-secondary);
  margin: 0;
`;

const ProfileLink = styled.a`
  font-family: "Space Mono", monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-accent);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const BIO_MAX = 800;
const CERTS = certsData.certifications;

// ─── Component ────────────────────────────────────────────────────────────────

const OnboardingForm = () => {
  // All hooks declared at top — above any early returns
  const { user, tags, role, loading } = useAuth();

  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    track: "devops",
    experienceYears: "",
    location: "",
    bio: "",
    skills: "",
  });
  const [assessments, setAssessments] = useState({});
  const [proofs, setProofs] = useState({});
  const [learningInterests, setLearningInterests] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | busy | done | error
  const [errorMsg, setErrorMsg] = useState("");

  // One-shot prefill from Firestore on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!user) return;
    const uid = user.uid;

    const prefill = async () => {
      try {
        // Load skills doc
        if (db) {
          const skillsSnap = await getDoc(doc(db, "skills", uid));
          if (skillsSnap.exists()) {
            const d = skillsSnap.data();
            if (d.assessments) setAssessments(d.assessments);
            if (Array.isArray(d.learningInterests)) setLearningInterests(d.learningInterests);
            if (d.proofs) {
              // Normalise to {status} shape
              const normalised = {};
              Object.entries(d.proofs).forEach(([k, v]) => {
                normalised[k] =
                  v && typeof v === "object" && v.status
                    ? { status: v.status }
                    : { status: "to_be_started" };
              });
              setProofs(normalised);
            }
          }
        }

        // Load user doc for profile fields
        if (db) {
          const userSnap = await getDoc(doc(db, "users", uid));
          if (userSnap.exists()) {
            const d = userSnap.data();
            setProfile((prev) => ({
              ...prev,
              track: d.track || prev.track,
              experienceYears: d.experienceYears != null ? String(d.experienceYears) : prev.experienceYears,
              location: d.location || prev.location,
              bio: d.bio || prev.bio,
              skills: Array.isArray(d.skills) ? d.skills.join(", ") : prev.skills,
            }));
          }
        }
      } catch (_) {
        // Prefill failure is non-fatal; the user can still fill the form
      }
    };

    prefill();
  }, [user]);

  // ── Guard: auth loading ───────────────────────────────────────────────────
  if (loading) {
    return null; // or a spinner; task says nothing/spinner
  }

  // ── Guard: not a junior and not admin ────────────────────────────────────
  const isJunior = Array.isArray(tags) && tags.includes("junior");
  const isAdmin = role === "admin";

  if (!isJunior && !isAdmin) {
    return (
      <Section>
        <Container>
          <Card>
            <Heading>Onboarding</Heading>
            <GateMessage>
              This questionnaire is for juniors. If you believe this is a
              mistake, contact your manager.
            </GateMessage>
          </Card>
        </Container>
      </Section>
    );
  }

  // ── Handlers ─────────────────────────────────────────────────────────────

  const setProfileField = (field) => (e) => {
    const val = e.target.value;
    setProfile((prev) => ({ ...prev, [field]: val }));
  };

  const setAssessment = (skill, level) => {
    setAssessments((prev) => ({ ...prev, [skill]: level }));
  };

  const cycleCertStatus = (certName) => {
    setProofs((prev) => {
      const current = (prev[certName] && prev[certName].status) || "to_be_started";
      const idx = STATUS_ORDER.indexOf(current);
      const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
      return { ...prev, [certName]: { status: next } };
    });
  };

  const onSubmit = async () => {
    setErrorMsg("");
    setStatus("busy");
    try {
      const skillsArray = profile.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        profile: {
          track: profile.track,
          experienceYears: profile.experienceYears !== "" ? Number(profile.experienceYears) : null,
          location: profile.location.trim() || null,
          bio: profile.bio.trim().slice(0, BIO_MAX) || null,
          skills: skillsArray,
        },
        assessments,
        proofs,
        learningInterests,
      };

      const result = await submitJuniorOnboarding(payload);
      if (result && result.ok) {
        setStatus("done");
      } else {
        throw new Error("Unexpected response from server.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg((err && err.message) || "Something went wrong. Please try again.");
    }
  };

  // ── Done state ───────────────────────────────────────────────────────────

  if (status === "done") {
    return (
      <Section>
        <Container>
          <Card>
            <Heading>All done!</Heading>
            <Sub>
              Your onboarding questionnaire has been saved. Your manager will
              review it and assign your learning path shortly.
            </Sub>
            <ProfileLink href="/profile">Go to my profile →</ProfileLink>
            <ProfileLink href="/guide">Read the onboarding guide →</ProfileLink>
          </Card>
        </Container>
      </Section>
    );
  }

  // ── Step renderers ───────────────────────────────────────────────────────

  const renderStep1 = () => (
    <>
      <Heading>Step 1 — Your Profile</Heading>
      <Sub>Tell us a bit about your background and what you are working toward.</Sub>

      <Field>
        <FieldLabel>Track</FieldLabel>
        <Select value={profile.track} onChange={setProfileField("track")}>
          <option value="devops">DevOps</option>
          <option value="sre">SRE</option>
          <option value="platform">Platform</option>
        </Select>
      </Field>

      <Field>
        <FieldLabel>Years of experience</FieldLabel>
        <Input
          type="number"
          min="0"
          max="50"
          value={profile.experienceYears}
          onChange={setProfileField("experienceYears")}
          placeholder="e.g. 1"
        />
      </Field>

      <Field>
        <FieldLabel>Location</FieldLabel>
        <Input
          type="text"
          value={profile.location}
          onChange={setProfileField("location")}
          placeholder="City, Country"
          maxLength={100}
        />
      </Field>

      <Field>
        <FieldLabel>Short bio (up to {BIO_MAX} chars)</FieldLabel>
        <Textarea
          value={profile.bio}
          onChange={(e) => {
            // Read the value synchronously — React 16 pools the synthetic
            // event, so e.target is null inside the async setState updater.
            const val = e.target.value.slice(0, BIO_MAX);
            setProfile((prev) => ({ ...prev, bio: val }));
          }}
          maxLength={BIO_MAX}
          placeholder="What you have shipped, where you got stuck, what you want to learn next."
        />
        <CharCount>
          {profile.bio.length} / {BIO_MAX}
        </CharCount>
      </Field>

      <Field>
        <FieldLabel>Key skills (comma-separated)</FieldLabel>
        <Input
          type="text"
          value={profile.skills}
          onChange={setProfileField("skills")}
          placeholder="Docker, Kubernetes, Terraform, AWS"
          maxLength={400}
        />
      </Field>

      <ButtonRow>
        <Button type="button" onClick={() => setStep(2)}>
          Next →
        </Button>
      </ButtonRow>
    </>
  );

  const renderStep2 = () => (
    <>
      <Heading>Step 2 — Skill Self-Assessment</Heading>
      <Sub>
        Rate your current level for each skill. Pick the level that best
        reflects where you are today, not where you aspire to be.
      </Sub>

      {SKILLS_INVENTORY.map((skill) => (
        <SkillRow key={skill}>
          <SkillName>{skill}</SkillName>
          <LevelButtons>
            {SKILL_LEVELS.map((level) => (
              <LevelBtn
                key={level}
                type="button"
                active={assessments[skill] === level}
                onClick={() => setAssessment(skill, level)}
              >
                {level}
              </LevelBtn>
            ))}
          </LevelButtons>
        </SkillRow>
      ))}

      <ButtonRow>
        <Button type="button" secondary onClick={() => setStep(1)}>
          ← Back
        </Button>
        <Button type="button" onClick={() => setStep(3)}>
          Next →
        </Button>
      </ButtonRow>
    </>
  );

  const renderStep3 = () => (
    <>
      <Heading>Step 3 — Certifications</Heading>
      <Sub>
        Click each badge to cycle through your status: Not started → In progress
        → Achieved. Skip any that are not relevant.
      </Sub>

      <CertGrid>
        {CERTS.map((cert) => {
          const currentStatus =
            (proofs[cert.name] && proofs[cert.name].status) || "to_be_started";
          return (
            <CertRow key={cert.name}>
              <CertInfo>
                <CertName>{cert.name}</CertName>
                <CertIssuer>{cert.issuer}</CertIssuer>
              </CertInfo>
              <StatusBadge
                type="button"
                status={currentStatus}
                onClick={() => cycleCertStatus(cert.name)}
              >
                {STATUS_LABEL[currentStatus]}
              </StatusBadge>
            </CertRow>
          );
        })}
      </CertGrid>

      <ButtonRow>
        <Button type="button" secondary onClick={() => setStep(2)}>
          ← Back
        </Button>
        <Button type="button" onClick={() => setStep(4)}>
          Next →
        </Button>
      </ButtonRow>
    </>
  );

  const renderStep4 = () => {
    const toggleInterest = (option) => {
      setLearningInterests((prev) =>
        prev.includes(option) ? prev.filter((x) => x !== option) : [...prev, option]
      );
    };

    return (
      <>
        <Heading>Step 4 — What do you want to learn?</Heading>
        <Sub>
          Select the technologies and topics you are most interested in learning.
          This helps us tailor your learning path.
        </Sub>

        {LEARNING_INTEREST_GROUPS.map((group) => (
          <div key={group.pillar}>
            <FieldLabel style={{ display: "block", marginBottom: 10 }}>{group.pillar}</FieldLabel>
            <LevelButtons style={{ flexWrap: "wrap", marginBottom: 16 }}>
              {group.options.map((option) => (
                <LevelBtn
                  key={option}
                  type="button"
                  active={learningInterests.includes(option)}
                  onClick={() => toggleInterest(option)}
                >
                  {option}
                </LevelBtn>
              ))}
            </LevelButtons>
          </div>
        ))}

        {errorMsg && <Message kind="error">{errorMsg}</Message>}

        <ButtonRow>
          <Button type="button" secondary onClick={() => setStep(3)}>
            ← Back
          </Button>
          <Button type="button" onClick={onSubmit} disabled={status === "busy"}>
            {status === "busy" ? "Saving…" : "Submit"}
          </Button>
        </ButtonRow>
      </>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <Section>
      <Container>
        <Card>
          <Instructions label="how onboarding works">
            <h4>Step 1 — Your profile</h4>
            <p>
              Choose your track (DevOps / SRE / Platform), enter your years of
              experience, location, a short bio, and a comma-separated list of
              your key skills. This populates your staffing card and your
              career-path data.
            </p>
            <h4>Step 2 — Skill self-assessment</h4>
            <p>
              Rate each skill as <code>beginner</code>, <code>intermediate</code>,
              or <code>advanced</code>. Be honest — this shapes the learning path
              your manager assigns you.
            </p>
            <h4>Step 3 — Certifications</h4>
            <p>
              Click each cert badge to cycle its status: Not started → In progress
              → Achieved. Skip certs that are not relevant to you.
            </p>
            <h4>Step 4 — What you want to learn</h4>
            <p>
              Pick the technologies, products and services you want to grow into,
              grouped by our service areas. This shapes your career direction —
              select as many as you like.
            </p>
            <h4>What happens on submit</h4>
            <p>
              Your answers are written to <code>skills/{"{uid}"}</code> in
              Firestore and a uid-linked card is created in the juniors staffing
              collection. Your manager will review it and assign your learning path.
            </p>
          </Instructions>
          <StepIndicator>Step {step} of 4</StepIndicator>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </Card>
      </Container>
    </Section>
  );
};

export default OnboardingForm;
